import { getCookie } from '../utils/cookies';

export type AppEnvironment = 'dev' | 'testing' | 'uat' | 'prod' | 'release';

const getEnvironment = (): AppEnvironment => {
	if (typeof window !== 'undefined') {
		const hostname = window.location.hostname;

		if (hostname.includes('release')) {
		  return 'release';
		}

		if (
			hostname === 'admin.worldservicesgroup.com' ||
			(hostname.includes('worldservicesgroup.com') && !hostname.includes('staging'))
		) {
			return 'prod';
		}

		if (hostname.includes('staging')) {
		  return 'dev';
		}
	}

	const envVar = process.env.REACT_APP_ENVIRONMENT;
	if (envVar === 'prod') {
	  return 'prod';
	}
	if (envVar === 'release') {
	  return 'release';
	}
	if (envVar === 'testing') {
	  return 'testing';
	}
	if (envVar === 'uat') {
	  return 'uat';
	}

	return 'dev';
};

const environment = getEnvironment();

/** Resolved once at app load from hostname, then REACT_APP_ENVIRONMENT at build time. */
export const getAppEnvironment = (): AppEnvironment => environment;

/** True only when running on the real PROD environment (env var or hostname). */
export const isProd = (): boolean => environment === 'prod';

/** Prod member site — env, build-time API URL, or live hostname. */
const isProductionMemberSite = (): boolean => {
	if (process.env.REACT_APP_ENVIRONMENT === 'prod' || isProd()) {
	  return true;
	}

	const apiRoot = (process.env.REACT_APP_ROOT_API ?? '').toLowerCase();
	if (apiRoot.includes('api.serverless.worldservicesgroup.com')) {
	  return true;
	}

	if (typeof window === 'undefined') {
	  return false;
	}

	const hostname = window.location.hostname;
	if (hostname.includes('staging') || hostname.includes('release')) {
	  return false;
	}

	return (
		hostname === 'admin.worldservicesgroup.com' ||
		(hostname.includes('worldservicesgroup.com') && !hostname.includes('staging'))
	);
};

/**
 * Office Main Contacts block on Manage Firm → Professionals.
 * Set REACT_APP_PROFESSIONALS_MAIN_CONTACT_ENABLED=false in prod to hide; remove or set true to show again.
 */
export const isProfessionalsMainContactEnabled = (): boolean => {
	if (!isProd()) {
	  return true;
	}

	const flag = process.env.REACT_APP_PROFESSIONALS_MAIN_CONTACT_ENABLED?.trim().toLowerCase();
	if (flag === 'false') {
	  return false;
	}
	return true;
};

/** Modern member search on all non-prod environments. Prod keeps the legacy iframe. */
export const isNewMemberSearchEnabled = (): boolean => {
	if (isProductionMemberSite()) {
	  return false;
	}

	const override = process.env.REACT_APP_NEW_MEMBER_SEARCH_ENABLED?.trim().toLowerCase();
	if (override === 'false') {
	  return false;
	}
	if (override === 'true') {
	  return true;
	}
	return true;
};

const LEGACY_STAFF_URLS = {
	prod: 'https://admin-legacy.worldservicesgroup.com',
	release: 'https://admin-release.worldservicesgroup.com',
	dev: 'https://admin-legacy.worldservicesgroup.com',
	testing: 'https://admin-legacy.worldservicesgroup.com',
	uat: 'https://admin-staging.worldservicesgroup.com',
};
export const LEGACY_STAFF_URL = LEGACY_STAFF_URLS[environment];


const LEGACY_MEMBER_URLS = {
	prod: 'https://members-legacy.worldservicesgroup.com',
	release: 'https://members-release.worldservicesgroup.com',
	dev: 'https://members-staging.worldservicesgroup.com',
	testing: 'https://members-staging.worldservicesgroup.com',
	uat: 'https://members-staging.worldservicesgroup.com',
};
export const LEGACY_MEMBER_URL = LEGACY_MEMBER_URLS[environment];

export const LEGACY_MEMBER_LOGIN_URL = `${LEGACY_MEMBER_URL}/set_cookie.asp`;
export const LEGACY_STAFF_LOGIN_URL = `${LEGACY_STAFF_URL}/set_cookie.asp`;

export const navigateToLegacy = (isStaffMode:boolean) => {
	const token = getCookie('token');
	const baseUrl = isStaffMode ? `${LEGACY_STAFF_URL}/wsg_admin1.asp` : `${LEGACY_MEMBER_URL}/my_wsg.asp`;
	const url = token ? `${baseUrl}?token=${encodeURIComponent(token)}` : baseUrl;
	const aTag = document.createElement('a');
	aTag.href = url;
	aTag.target = '_blank';
	aTag.click();
}

const tokenCookie = getCookie('token');

export const LegacyRedirection = {
	'/staff/groups': `${LEGACY_STAFF_URL}/wsg_admin15.asp`,
	'/staff/events': `${LEGACY_STAFF_URL}/meetings_admin.asp`,
	'/staff/manage-profile': `${LEGACY_STAFF_URL}/wsg_admin22.asp`,
	'/staff/lists': `${LEGACY_STAFF_URL}/admin_invited.asp?action=page&pcomp=10435&show=m`,

	'/staff/send-grid': `${LEGACY_STAFF_URL}/dashboard_sendgrid.asp`,

	'/staff/overview/firm': `${LEGACY_STAFF_URL}/dashboard_powerbi.asp?rt=stats`,
	'/staff/overview/events': `${LEGACY_STAFF_URL}/dashboard_powerbi.asp?rt=events`,
	'/staff/overview/referrals': `${LEGACY_STAFF_URL}/dashboard_powerbi.asp?rt=referrals`,
	'/staff/overview/dues': `${LEGACY_STAFF_URL}/dashboard_powerbi.asp?rt=dues`,

	'/staff/google/wsg-site': `${LEGACY_STAFF_URL}/dashboard_ganalytics.asp?rt=all`,
	'/staff/google/firm-profile': `${LEGACY_STAFF_URL}/dashboard_ganalytics.asp?rt=firm`,
	'/staff/google/professional-profile': `${LEGACY_STAFF_URL}/dashboard_ganalytics.asp?rt=pro`,
	'/staff/google/article': `${LEGACY_STAFF_URL}/dashboard_ganalytics.asp?rt=article`,
	'/staff/google/campaigns': `${LEGACY_STAFF_URL}/dashboard_ganalytics.asp?rt=campaign`,

	'/staff/firm-score': `${LEGACY_STAFF_URL}/admin_firmsocialmedia.asp`,
	'/staff/practice-head': `${LEGACY_STAFF_URL}/admin_firmpracticeheads.asp`,
	'/staff/wsg-contacts': `${LEGACY_STAFF_URL}/admin_wsgcontacts.asp`,

	'/staff/firm-referrals': `${LEGACY_STAFF_URL}/admin_manage_referrals.asp`,
	'/staff/secondments': `${LEGACY_STAFF_URL}/admin_manage_secondments.asp`,
	'/staff/dealmaker': `${LEGACY_STAFF_URL}/admin_manage_dealmaker.asp`,

	'/staff/assessment-fee': `${LEGACY_STAFF_URL}/admin_assessmentfees.asp`,
	'/staff/guides': `${LEGACY_STAFF_URL}/admin_manage_bizguide.asp`,
	'/staff/member-to-member': `${LEGACY_STAFF_URL}/membertomember.aspx?eventid=2437`,

	'/staff/sync-module': `${LEGACY_STAFF_URL}/admin_importmodule.asp`,
	'/staff/press-sync': `${LEGACY_STAFF_URL}/admin_rssfeedsync.asp`,
	'/staff/rss-feed': `${LEGACY_STAFF_URL}/admin_Rssfeeds.asp`,
	
	'/staff/how-to': `${LEGACY_STAFF_URL}/howto_admin.asp`,
	'/staff/faq': `${LEGACY_STAFF_URL}/faq_admin.asp`,

	'/staff/articles': `${LEGACY_STAFF_URL}/wsg_admin12.asp`,
	'/staff/release': `${LEGACY_STAFF_URL}/wsg_admin13.asp`,
	'/staff/collaborations': `${LEGACY_STAFF_URL}/wsg_admin27.asp`,

  '/staff/accounting': `${LEGACY_STAFF_URL}/financial.asp`,
  '/staff/send-emails': `${LEGACY_STAFF_URL}/wsg_submitemails.asp`,
  '/staff/design-email': `${LEGACY_STAFF_URL}/wsg_admin.asp?action=manageemail`,
  '/staff/unsubscriber-list': `${LEGACY_STAFF_URL}/admin_unsubscribe.asp`,
  '/staff/trigger-email-templates': `${LEGACY_STAFF_URL}/trigger_emails.asp`,
  
  '/staff/blogroll': `${LEGACY_STAFF_URL}/admin_blogrolls.asp`,
  '/staff/blog-posts': `${LEGACY_STAFF_URL}/admin_manage_blogposts.asp`,

  '/staff/newsletter': `${LEGACY_STAFF_URL}/wsg_admin16.asp`,

  '/staff/bod-council': `${LEGACY_STAFF_URL}/bodupdate.aspx?action=1`,
  '/staff/wsg-objectives': `${LEGACY_STAFF_URL}/believeupdate.aspx?action=2`,
  '/staff/home-page-update': `${LEGACY_STAFF_URL}/wsg_admin27c.asp`,
  '/staff/business-guide-upload': `${LEGACY_STAFF_URL}/wsg_adminguides.asp`,
  
  '/staff/stats': `${LEGACY_STAFF_URL}/wsg_adminstat.asp`,
  '/staff/listing': `${LEGACY_STAFF_URL}/listing_edit.asp`,
  '/staff/trigger': `${LEGACY_STAFF_URL}/wsg_admin17.asp`,
  '/staff/history-logs': `${LEGACY_STAFF_URL}/history_logs.asp`,
  '/staff/db': `${LEGACY_STAFF_URL}/admin_database.asp`,
  
  '/staff/notification-email': `${LEGACY_STAFF_URL}/admin_notificationemails.asp`,
  '/staff/networkwire-tool': `${LEGACY_STAFF_URL}/admin_manage_networkwire.asp`,

  '/staff/meta-tags/firm': `${LEGACY_STAFF_URL}/admin_manage_metadata.asp`,
  '/staff/meta-tags/professional': `${LEGACY_STAFF_URL}/admin_professional_metadata.asp`,

	//#region member site
  
	'/': `${LEGACY_MEMBER_URL + (tokenCookie 
    ? `/?loginUrl=${window.location.origin}` 
    : `/default.asp?loginUrl=${window.location.origin}/login` 
  )}`,
	'/logout': `${LEGACY_MEMBER_URL}/login.asp?action=logoff`,
	'/mywsg': `${LEGACY_MEMBER_URL}/my_wsg.asp`,
	'/referrals': `${LEGACY_MEMBER_URL}/manage_referrals.asp`,
	'/inbox': `${LEGACY_MEMBER_URL}/wsg_mailbox.asp`,
	'/staff/mywsg': `${LEGACY_MEMBER_URL}/my_wsg.asp`,
	'/staff/about-us': `${LEGACY_MEMBER_URL}/aboutus.asp`,
	'/about-us': `${LEGACY_MEMBER_URL}/aboutus.asp`,
	'/staff/members': `${LEGACY_MEMBER_URL}/wsg_members.asp`,
	'/members': `${LEGACY_MEMBER_URL}/wsg_members.asp`,
	'/groups': `${LEGACY_MEMBER_URL}/wsg_groups.asp`,
	'/meetings': `${LEGACY_MEMBER_URL}/meetings.asp`,
	'/meeting2': `${LEGACY_MEMBER_URL}/meeting2.asp`,
	'/meetingaccomodation': `${LEGACY_MEMBER_URL}/meetingaccomodation`,
	'/meetingregistration': `${LEGACY_MEMBER_URL}/meetingregistration`,
  
	'/gc-insights': `${LEGACY_MEMBER_URL}/gcinsights.asp`,
	'/member-blogs': `${LEGACY_MEMBER_URL}/blog`,
	'/featured': `${LEGACY_MEMBER_URL}/blog/featuredblog.aspx`,
	'/staff/gc-insights': `${LEGACY_MEMBER_URL}/gcinsights.asp`,
	'/staff/member-blogs': `${LEGACY_MEMBER_URL}/blog`,
	'/staff/featured': `${LEGACY_MEMBER_URL}/blog/featuredblog.aspx`,

	'/wsg-blog': `${LEGACY_MEMBER_URL}/blog/default.aspx`,
	'/wsg-knowledge-center': `${LEGACY_MEMBER_URL}/wsg-knowledge-center.asp`,
	'/secondments': `${LEGACY_MEMBER_URL}/secondments.asp`,
	'/dealmakers': `${LEGACY_MEMBER_URL}/dealmaker.asp`,
	'/staff/wsg-blog': `${LEGACY_MEMBER_URL}/blog/default.aspx`,
	'/staff/wsg-knowledge-center': `${LEGACY_MEMBER_URL}/wsg-knowledge-center.asp`,
	'/staff/dealmakers': `${LEGACY_MEMBER_URL}/dealmaker.asp`,

	'/release': `${LEGACY_MEMBER_URL}/releases.asp`,
	'/collaborations': `${LEGACY_MEMBER_URL}/success_stories.asp`,
	'/newsletters': `${LEGACY_MEMBER_URL}/networkwire_1q2026.asp`,
	'/latest-news': `${LEGACY_MEMBER_URL}/newsfeeds`,
	'/submit-article': `${LEGACY_MEMBER_URL}/submitarticle.asp?action=add`,
	'/submit-release': `${LEGACY_MEMBER_URL}/submitrelease.asp?action=add`,
	'/submit-event': `${LEGACY_MEMBER_URL}/manage_events.asp?action=add`,
	'/staff/newsletters': `${LEGACY_MEMBER_URL}/networkwire_1q2026.asp`,
	'/staff/latest-news': `${LEGACY_MEMBER_URL}/newsfeeds`,
	'/staff/submit-article': `${LEGACY_MEMBER_URL}/submitarticle.asp?action=add`,
	'/staff/submit-release': `${LEGACY_MEMBER_URL}/submitrelease.asp?action=add`,
	'/staff/submit-event': `${LEGACY_MEMBER_URL}/manage_events.asp?action=add`,

	'/leadership-program': `${LEGACY_MEMBER_URL}/wsglearninginstitute_growthplay.asp`,
	'/marketing-workshop': `${LEGACY_MEMBER_URL}/wsglearninginstitute_marketingworkshop.asp`,
	'/emeritus-program': `${LEGACY_MEMBER_URL}/wsglearninginstituteoverview.asp`,
	'/staff/leadership-program': `${LEGACY_MEMBER_URL}/wsglearninginstitute_growthplay.asp`,
	'/staff/marketing-workshop': `${LEGACY_MEMBER_URL}/wsglearninginstitute_marketingworkshop.asp`,
	'/staff/emeritus-program': `${LEGACY_MEMBER_URL}/wsglearninginstituteoverview.asp`,

	'/covid-19': `${LEGACY_MEMBER_URL}/covid19_memberinsights.asp`,
	'/ukraine-crisis': `${LEGACY_MEMBER_URL}/hub-resource-center.asp`,
	'/staff/covid-19': `${LEGACY_MEMBER_URL}/covid19_memberinsights.asp`,
	'/staff/ukraine-crisis': `${LEGACY_MEMBER_URL}/hub-resource-center.asp`,
  '/articles': `${LEGACY_MEMBER_URL}/publications.asp`,
  '/blog-posts': `${LEGACY_MEMBER_URL}/blog`,
  '/advance-search': isNewMemberSearchEnabled()
    ? '/advanced-member-search'
    : '/advanced-search.asp',
  '/staff/advance-search': isNewMemberSearchEnabled()
    ? '/advanced-member-search'
    : '/advanced-search.asp',
  
  '/dashboard': `${LEGACY_MEMBER_URL}/dashboard.asp`,
  '/wsg_firmprofessionals': `${LEGACY_MEMBER_URL}/wsg_firmprofessionals.asp`,
  '/manage_publications': `${LEGACY_MEMBER_URL}/manage_publications.asp`,
  '/manage_releases': `${LEGACY_MEMBER_URL}/manage_releases.asp`,
  '/manage_events': `${LEGACY_MEMBER_URL}/manage_events.asp`,
  '/manage_referrals': `${LEGACY_MEMBER_URL}/manage_referrals.asp`,
  '/manage_blogrolls': `${LEGACY_MEMBER_URL}/manage_blogrolls.asp`,
  '/history': `${LEGACY_MEMBER_URL}/history.asp`,
  '/manage_doingbizguide': `${LEGACY_MEMBER_URL}/manage_doingbizguide.asp`,
  '/manage_practiceheads': `${LEGACY_MEMBER_URL}/manage_practiceheads.asp`,
  '/manage_secondments': `${LEGACY_MEMBER_URL}/manage_secondments.asp`,
  '/manage_dealmaker': `${LEGACY_MEMBER_URL}/manage_dealmaker.asp`,

	//#endregion
}
